import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    Typography,
} from '@mui/material';
import { Plus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { fetchDownloadDicomSeries } from '~/backend/connector-api/connector-components';
import CollapsibleBottomPanel from '~/components/CollapsibleBottomPanel';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import { LabeledValue } from '~/components/Fields/LabeledValue';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import ReviewStatus from '~/components/Structures/ReviewStatus';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import {
    domainEventBus,
    RegistrationApprovalStatusChangeRequestedEvent,
    RegistrationCreateRequestedEvent,
    RegistrationDeleteRequestedEvent,
    RegistrationRenameRequestedEvent,
} from '~/domain/events';
import { Image3D } from '~/domain/patient/image3d';
import { RegistrationViewModel } from '~/domain/readmodels/RegistrationReadModel';
import { AvailableImage } from '~/hooks/image/usePrimaryImage';
import useBusyIndicator from '~/hooks/useBusyIndicator';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import {
    useActivateRegistration,
    useRegistrationState,
    useSelectRegistration,
    useSelectedRegistrationObject,
} from '~/hooks/registration/useRegistrationStore';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { environmentState } from '~/state/atoms/engineAtoms';
import { autoRegistrationInProgressState } from '~/state/atoms/toolsAtoms';
import { GridLayoutMode, gridLayoutModeState } from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import { formatMmAsCm } from '~/utils/units';
import { formatDateString } from '~/utils/date';
import { decomposeRigidTransform } from '~/lib/elastix/utils';
import RegistrationHeader from './RegistrationHeader';
import RegistrationMenu from './RegistrationMenu';

interface RegistrationPanelProps {
    image: AvailableImage;
    primaryImage: Image3D;
    isPrimary?: boolean;
    onClose: () => void;
    isExpanded?: boolean;
    onToggleExpanded?: () => void;
}

export default function RegistrationPanel({
    image,
    primaryImage,
    isPrimary = false,
    onClose,
    isExpanded = true,
    onToggleExpanded,
}: RegistrationPanelProps) {
    const gridLayoutMode = useRecoilValue(gridLayoutModeState);
    const selectRegistration = useSelectRegistration();
    const activateRegistration = useActivateRegistration();
    const { removeEnvironmentSecondaryImage } = useViewportEnvironment();
    const registrationState = useRegistrationState();
    const { processImportedFiles } = useProcessDicomFiles();
    const { showBusy, hideBusy } = useBusyIndicator();
    const isAutoRegistering = useRecoilValue(autoRegistrationInProgressState);

    const [isRenaming, setIsRenaming] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const availableRegistrations = useMemo(() => {
        const imageReg = registrationState.imageRegistrations.find(
            (ir) => ir.imageSeriesUid === image.seriesInstanceUID
        );
        return imageReg?.registrations ?? [];
    }, [registrationState.imageRegistrations, image.seriesInstanceUID]);

    const activeRegistration = useMemo(() => {
        if (!registrationState.selectedSopInstanceUid) {
            return null;
        }
        const found = availableRegistrations.find(
            (r) => r.sopInstanceUid === registrationState.selectedSopInstanceUid
        );
        return found ?? null;
    }, [availableRegistrations, registrationState.selectedSopInstanceUid]);

    const isApproved = activeRegistration?.isApproved ?? false;

    const sharedFrameOfReference = useMemo(() => {
        if (!image.imageObject) {
            return false;
        }
        return (
            primaryImage.frameOfReferenceUid ===
            image.imageObject.frameOfReferenceUid
        );
    }, [primaryImage, image]);

    // Don't show panel for primary images or in side-by-side mode
    if (isPrimary || gridLayoutMode !== GridLayoutMode.Default) {
        return null;
    }

    const handleCreate = async () => {
        // If the image hasn't been DICOM-loaded yet (e.g. a
        // session secondary image), download and process it
        // first so the registration store can resolve it.
        if (!image.isLoaded) {
            setIsDownloading(true);
            await showBusy('Downloading image...');
            try {
                const dicomBlob = await fetchDownloadDicomSeries({
                    pathParams: {
                        studyId: image.studyInstanceUID,
                        seriesId: image.seriesInstanceUID,
                    },
                    queryParams: { series: true },
                });
                const files = [
                    new File([dicomBlob], `${image.seriesInstanceUID}.zip`),
                ];
                const result = await processImportedFiles(files);
                if (!result?.images?.length) {
                    return;
                }
            } catch (error) {
                handleError(error, 'Failed to download image');
                return;
            } finally {
                setIsDownloading(false);
                hideBusy();
            }
        }

        domainEventBus.publish(
            new RegistrationCreateRequestedEvent(
                primaryImage.seriesUid,
                image.seriesInstanceUID
            )
        );
    };

    const handleRename = (newLabel: string) => {
        if (activeRegistration) {
            domainEventBus.publish(
                new RegistrationRenameRequestedEvent(
                    activeRegistration.sopInstanceUid,
                    newLabel
                )
            );
        }
    };

    const handleDelete = () => {
        if (activeRegistration && !isApproved) {
            domainEventBus.publish(
                new RegistrationDeleteRequestedEvent(
                    activeRegistration.sopInstanceUid
                )
            );
            selectRegistration(undefined);
            removeEnvironmentSecondaryImage();
        }
        setIsDeleteDialogOpen(false);
    };

    const headerActions = activeRegistration ? (
        <RegistrationMenu
            isApproved={isApproved}
            isDisabled={isAutoRegistering}
            hasSharedFrameOfReference={sharedFrameOfReference}
            availableRegistrations={availableRegistrations}
            activeRegistrationSopUid={activeRegistration.sopInstanceUid}
            onRename={() => setIsRenaming(true)}
            onCreateNew={handleCreate}
            onDelete={() => setIsDeleteDialogOpen(true)}
            onChangeRegistration={(sopUid) =>
                activateRegistration(sopUid, image.seriesInstanceUID)
            }
        />
    ) : null;

    return (
        <>
            <CollapsibleBottomPanel
                header={
                    <RegistrationHeader
                        activeRegistration={activeRegistration}
                        onRename={handleRename}
                        isRenaming={isRenaming}
                        onStartRename={() => setIsRenaming(true)}
                        onCancelRename={() => setIsRenaming(false)}
                    />
                }
                headerActions={headerActions}
                isExpanded={isExpanded}
                onToggleExpanded={onToggleExpanded}
                onClose={onClose}
            >
                {!activeRegistration ? (
                    <Stack spacing={1}>
                        <Button
                            data-testid="create-registration-button"
                            variant="contained"
                            startIcon={
                                isDownloading ? (
                                    <CircularProgress size={14} />
                                ) : (
                                    <Plus size={14} />
                                )
                            }
                            onClick={handleCreate}
                            disabled={
                                sharedFrameOfReference ||
                                isDownloading ||
                                isAutoRegistering
                            }
                            fullWidth
                        >
                            <FormattedMessage
                                id={
                                    isDownloading
                                        ? 'downloadingImage'
                                        : 'registrationNew'
                                }
                            />
                        </Button>
                        {sharedFrameOfReference && (
                            <Typography
                                variant="body2"
                                fontSize={12}
                                color="text.secondary"
                            >
                                <FormattedMessage id="registration.sharedForNoRegistration" />
                            </Typography>
                        )}
                    </Stack>
                ) : (
                    <RegistrationDetails
                        primaryImage={primaryImage}
                        image={image}
                        registration={activeRegistration}
                    />
                )}
            </CollapsibleBottomPanel>

            <ConfirmationDialog
                open={isDeleteDialogOpen}
                title={<FormattedMessage id="deleteRegistration" />}
                message={<FormattedMessage id="deleteRegistrationConfirm" />}
                onConfirm={handleDelete}
                onCancel={() => setIsDeleteDialogOpen(false)}
                confirmText={<FormattedMessage id="delete" />}
                cancelText={<FormattedMessage id="cancel" />}
                mode="error"
            />
        </>
    );
}

interface RegistrationDetailsProps {
    primaryImage: Image3D;
    image: AvailableImage;
    registration: RegistrationViewModel;
}

function RegistrationDetails({
    primaryImage,
    image,
    registration,
}: RegistrationDetailsProps) {
    const intl = useIntl();
    const registrationObject = useSelectedRegistrationObject();
    const environment = useRecoilValue(environmentState);

    const [pendingStatus, setPendingStatus] =
        useState<DICOMApprovalStatus | null>(null);

    const primaryLabel = useMemo(() => {
        return primaryImage.seriesMetadata.seriesDescription || 'Primary';
    }, [primaryImage]);

    const secondaryLabel = useMemo(() => {
        if (image.imageObject) {
            return (
                image.imageObject.seriesMetadata.seriesDescription ||
                image.label ||
                'Secondary'
            );
        }
        return image.label || 'Secondary';
    }, [image]);

    // Determine source/target labels from the actual registration FOR UIDs
    // rather than assuming primary is always source
    const { sourceLabel, targetLabel } = useMemo(() => {
        const primaryIsSource =
            registration.sourceFrameOfReferenceUid ===
            primaryImage.frameOfReferenceUid;
        return {
            sourceLabel: primaryIsSource ? primaryLabel : secondaryLabel,
            targetLabel: primaryIsSource ? secondaryLabel : primaryLabel,
        };
    }, [registration, primaryImage, primaryLabel, secondaryLabel]);

    const sharedFrameOfReference = useMemo(() => {
        if (!image.imageObject) {
            return false;
        }
        return (
            primaryImage.frameOfReferenceUid ===
            image.imageObject.frameOfReferenceUid
        );
    }, [primaryImage, image]);

    const computeTransform = useCallback(() => {
        if (!registrationObject) {
            return null;
        }
        return decomposeRigidTransform(registrationObject.transformation);
    }, [registrationObject]);

    const [transformData, setTransformData] = useState(computeTransform);

    useEffect(() => {
        if (!environment) {
            return;
        }
        const handler = () => setTransformData(computeTransform());
        environment.eventTarget.addEventListener(
            'registration_changed',
            handler
        );
        return () => {
            environment.eventTarget.removeEventListener(
                'registration_changed',
                handler
            );
        };
    }, [environment, computeTransform]);

    useEffect(() => {
        setTransformData(computeTransform());
    }, [computeTransform]);

    const formatTranslationCm = (v: [number, number, number]) =>
        `${formatMmAsCm(v[0])}, ${formatMmAsCm(v[1])}, ${formatMmAsCm(v[2])}`;

    const formatAnglesDeg = (v: [number, number, number]) =>
        `${v[0].toFixed(1)}, ${v[1].toFixed(1)}, ${v[2].toFixed(1)}`;

    const handleApprovalStatusChange = (
        e: SelectChangeEvent<DICOMApprovalStatus>
    ) => {
        const newStatus = e.target.value as DICOMApprovalStatus;
        if (newStatus !== registration.approvalStatus) {
            setPendingStatus(newStatus);
        }
    };

    const confirmApprovalStatusChange = () => {
        if (pendingStatus) {
            domainEventBus.publish(
                new RegistrationApprovalStatusChangeRequestedEvent(
                    registration.sopInstanceUid,
                    pendingStatus
                )
            );
        }
        setPendingStatus(null);
    };

    const allStatuses = Object.values(DICOMApprovalStatus);
    const currentStatus = allStatuses.includes(registration.approvalStatus)
        ? registration.approvalStatus
        : DICOMApprovalStatus.UNAPPROVED;

    return (
        <Stack spacing={0.5} display="grid">
            {registration.contentDescription?.length > 0 && (
                <Typography variant="body2" fontSize={13} sx={{ pb: 0.5 }}>
                    <SensitiveInfoTypography>
                        {registration.contentDescription}
                    </SensitiveInfoTypography>
                </Typography>
            )}

            <LabeledValue
                label={intl.formatMessage({
                    id: 'registration.sourceImage',
                })}
                value={sourceLabel}
                inline
                sensitive
            />
            <LabeledValue
                label={intl.formatMessage({
                    id: 'registration.targetImage',
                })}
                value={targetLabel}
                inline
                sensitive
            />
            {sharedFrameOfReference && (
                <LabeledValue
                    label={intl.formatMessage({
                        id: 'frameOfReference',
                    })}
                    value={intl.formatMessage({
                        id: 'registration.sharedFor',
                    })}
                    inline
                />
            )}
            {registration.contentCreatorName && (
                <LabeledValue
                    label={intl.formatMessage({
                        id: 'registration.contentCreator',
                    })}
                    value={
                        registration.contentCreatorName.length > 0
                            ? registration.contentCreatorName
                            : 'Unknown'
                    }
                    inline
                />
            )}
            {registration.contentDate && (
                <LabeledValue
                    label={intl.formatMessage({
                        id: 'registration.contentDate',
                    })}
                    value={
                        formatDateString(registration.contentDate) ||
                        registration.contentDate
                    }
                    inline
                    sensitive
                />
            )}
            {transformData && (
                <>
                    <LabeledValue
                        label={intl.formatMessage({
                            id: 'registration.translation',
                        })}
                        value={formatTranslationCm(transformData.translationMm)}
                        inline
                    />
                    <LabeledValue
                        label={intl.formatMessage({
                            id: 'registration.eulerAngles',
                        })}
                        value={formatAnglesDeg(transformData.eulerAnglesDeg)}
                        inline
                    />
                </>
            )}
            <Box>
                <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
                    <InputLabel id="registration-approval-status-label" shrink>
                        <FormattedMessage id="approvalStatus" />
                    </InputLabel>
                    <Select
                        labelId="registration-approval-status-label"
                        label={<FormattedMessage id="approvalStatus" />}
                        value={currentStatus}
                        onChange={handleApprovalStatusChange}
                        renderValue={(selected) => (
                            <ReviewStatus status={selected} fontSize={14} />
                        )}
                    >
                        {allStatuses
                            .filter(
                                (status) =>
                                    status !== registration.approvalStatus
                            )
                            .map((status) => (
                                <MenuItem
                                    key={status}
                                    value={status}
                                    sx={{ fontSize: '0.875rem' }}
                                >
                                    <ReviewStatus
                                        status={status}
                                        fontSize={14}
                                    />
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl>
            </Box>

            <ConfirmationDialog
                open={pendingStatus !== null}
                title={
                    <FormattedMessage id="registration.confirmApprovalStatusChange" />
                }
                message={
                    <FormattedMessage
                        id="registration.approvalStatusChangeDescription"
                        values={{
                            newApprovalStatus: pendingStatus
                                ? intl.formatMessage({
                                      id:
                                          pendingStatus ===
                                          DICOMApprovalStatus.APPROVED
                                              ? 'approved'
                                              : pendingStatus ===
                                                  DICOMApprovalStatus.REJECTED
                                                ? 'rejected'
                                                : 'unapproved',
                                  })
                                : '',
                        }}
                    />
                }
                onConfirm={confirmApprovalStatusChange}
                onCancel={() => setPendingStatus(null)}
                confirmText={<FormattedMessage id="confirm" />}
                cancelText={<FormattedMessage id="cancel" />}
                mode="primary"
            />
        </Stack>
    );
}
